import React, { useEffect, useState } from "react";
import { trackAndRedirect } from "../../lib/track";
import {
    Bell,
    CheckCircle2,
    Clock,
    Zap,
    Shield,
    Smartphone,
    PlayCircle,
    Sparkles,
    Quote,
    AlertTriangle,
    ArrowRight,
    Users,
    LineChart,
    Layers,
    Target,
    Coffee,
    Workflow,
    Star,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../../components/ui/accordion";

const heroBg =
    "https://static.prod-images.emergentagent.com/jobs/c97944d1-40eb-4223-b474-bd3b37680737/images/81ae99c98e0a486b6bac5c4ce18c113b6c21237fa317db6a0647fcce4d8b200b.png";
const timelineBg =
    "https://static.prod-images.emergentagent.com/jobs/c97944d1-40eb-4223-b474-bd3b37680737/images/b108e554fd416f2961745614dd7a5772932d32de5249780a0d42e4f3bee8c859.png";
const traders = [
    "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB0cmFkZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzkwOTIzMDd8MA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/flagged/photo-1579225818168-858da8667fae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjB0cmFkZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzkwOTIzMDd8MA&ixlib=rb-4.1.0&q=85",
    "https://images.pexels.com/photos/7567588/pexels-photo-7567588.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.unsplash.com/photo-1675869940341-d495d49010b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjB0cmFkZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzkwOTIzMDd8MA&ixlib=rb-4.1.0&q=85",
];

// ---------- Reusable buttons ----------
function CTA({
    location,
    label = "Start 5-Day Trial",
    section,
    variant = "primary",
    icon = true,
    className = "",
}) {
    const handle = (e) => {
        e.preventDefault();
        trackAndRedirect(location, { label, section });
    };
    const cls =
        variant === "primary"
            ? "btn-primary"
            : variant === "ghost-dark"
              ? "btn-ghost-dark"
              : "btn-ghost-light";
    return (
        <button
            type="button"
            onClick={handle}
            data-testid={`cta-${location}`}
            className={`${cls} ${className}`}
        >
            <span>{label}</span>
            {icon && <ArrowRight className="w-4 h-4" />}
        </button>
    );
}

// ---------- Sections ----------
function Nav() {
    return (
        <nav
            data-testid="top-nav"
            className="absolute top-0 left-0 right-0 z-30 px-4 sm:px-8 py-5 flex items-center justify-between text-white"
        >
            <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-[var(--mbe-gold)] grid place-items-center text-[#0a0d14]">
                    <Bell className="w-5 h-5" strokeWidth={2.5} />
                </div>
                <span className="font-display text-lg font-bold tracking-tight">
                    MBE <span className="text-[var(--mbe-gold)]">Alert</span>
                </span>
            </div>
            <div className="hidden md:flex items-center gap-7 text-sm text-slate-300">
                <a href="#features" className="hover:text-white transition">Features</a>
                <a href="#bonus" className="hover:text-white transition">Bonus</a>
                <a href="#faq" className="hover:text-white transition">FAQ</a>
            </div>
            <CTA
                location="nav-trial"
                label="Start Trial"
                section="nav"
                className="text-sm !py-2.5 !px-5"
            />
        </nav>
    );
}

function HeroSection() {
    return (
        <section
            data-testid="section-hero"
            className="relative bg-[var(--mbe-bg-dark)] text-white overflow-hidden"
            style={{
                backgroundImage: `linear-gradient(180deg, rgba(10,13,20,0.85) 0%, rgba(10,13,20,0.97) 70%, #0a0d14 100%), url(${heroBg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            <div className="bg-grid-dark absolute inset-0 opacity-50" />
            <Nav />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 md:pt-40 md:pb-32 grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7 fade-in-up">
                    <span className="tag-pill bg-[rgba(229,169,61,0.12)] text-[var(--mbe-gold)] border border-[rgba(229,169,61,0.3)]">
                        <span className="pulse-dot relative w-1.5 h-1.5 rounded-full bg-[var(--mbe-green)]" />
                        Live Telegram Alerts · MBE System
                    </span>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mt-6">
                        Stop Watching The Charts,
                        <br />
                        <span className="text-[var(--mbe-gold)] gold-text-glow">
                            Start Catching Setups!
                        </span>
                    </h1>
                    <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
                        MBE Alert notifies you on Telegram the moment an entry zone
                        is being triggered. No more need for constant chart
                        monitoring; just wait for an alert.
                    </p>
                    <div className="mt-9 flex flex-col sm:flex-row gap-3">
                        <CTA location="hero-primary" section="hero" label="Start 5-Day Trial" />
                        <a
                            href="#how-it-works"
                            data-testid="hero-secondary-link"
                            className="btn-ghost-dark"
                        >
                            <PlayCircle className="w-4 h-4" />
                            See How It Works
                        </a>
                    </div>
                </div>

                {/* Telegram alert mock */}
                <div className="lg:col-span-5 relative fade-in-up">
                    <div className="absolute -inset-6 bg-[var(--mbe-gold)]/10 blur-3xl rounded-full" />
                    <div className="relative bg-[rgba(19,24,36,0.85)] backdrop-blur-xl border border-white/10 rounded-3xl p-5 shadow-2xl">
                        <div className="flex items-center gap-3 pb-4 border-b border-white/10">
                            <div className="w-10 h-10 rounded-full bg-[var(--mbe-gold)] grid place-items-center text-[#0a0d14] font-bold">
                                M
                            </div>
                            <div className="flex-1">
                                <p className="font-semibold text-sm">MBE Alert</p>
                                <p className="text-xs text-slate-400 flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                                    Online · auto delivery
                                </p>
                            </div>
                            <span className="text-xs text-slate-400">now</span>
                        </div>

                        <AlertCard
                            pair="XAU/USD"
                            tf="H1"
                            entry="2412.80"
                            time="now"
                            type="BUY"
                        />
                        <AlertCard
                            pair="EUR/USD"
                            tf="M30"
                            entry="1.08245"
                            time="3m ago"
                            type="SELL"
                        />
                        <AlertCard
                            pair="USD/JPY"
                            tf="H4"
                            entry="156.412"
                            time="18m ago"
                            type="BUY"
                            dim
                        />
                    </div>
                </div>
            </div>

            {/* Marquee logo strip */}
            <div className="relative border-t border-white/5 bg-black/40 backdrop-blur">
                <div className="overflow-hidden no-scrollbar">
                    <div className="marquee-track flex gap-12 py-5 px-6 whitespace-nowrap text-slate-500 text-xs uppercase tracking-[0.2em]">
                        {Array.from({ length: 2 }).flatMap((_, i) =>
                            [
                                "Built for MBE traders",
                                "Real-time delivery",
                                "Pair · Timeframe · Entry",
                                "No noise · only valid zones",
                                "5,000+ active members",
                                "Available globally",
                            ].map((t, j) => (
                                <span key={`${i}-${j}`} className="opacity-80">
                                    {t} <span className="text-[var(--mbe-gold)] mx-3">/</span>
                                </span>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function AlertCard({ pair, tf, entry, time, type, dim }) {
    const isBuy = type === "BUY";
    return (
        <div
            className={`mt-3 rounded-2xl border border-white/10 p-4 ${dim ? "opacity-60" : "bg-[rgba(10,13,20,0.6)]"}`}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span
                        className={`text-[11px] font-bold px-2 py-0.5 rounded ${isBuy ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}
                    >
                        {type}
                    </span>
                    <span className="font-display font-bold tracking-wide">{pair}</span>
                </div>
                <span className="text-[11px] text-slate-500">{time}</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                <Stat label="Pair" value={pair} />
                <Stat label="Timeframe" value={tf} />
                <Stat label="Entry" value={entry} highlight />
            </div>
        </div>
    );
}

function Stat({ label, value, highlight }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500">
                {label}
            </p>
            <p
                className={`mt-1 font-mono font-semibold ${highlight ? "text-[var(--mbe-gold)]" : "text-white"}`}
            >
                {value}
            </p>
        </div>
    );
}

function VideoEvolutionSection() {
    return (
        <section
            data-testid="section-evolution"
            id="how-it-works"
            className="bg-white text-[var(--mbe-text-light)] py-24 lg:py-32"
        >
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <span className="tag-pill bg-slate-100 text-slate-600">
                    The MBE Evolution
                </span>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-5 leading-tight">
                    From spotting setups too late
                    <br className="hidden sm:block" /> to catching them in real time.
                </h2>
                <p className="mt-5 text-slate-600 max-w-2xl mx-auto">
                    The MBE indicator shows entry orders, where peak buying
                    pressures and peak selling pressures are present. MBE Alert
                    tightens the gap between the chart and your reaction, so you
                    no longer miss a clean entry zone again, no matter if
                    you&apos;re in a meeting, on a train, or asleep.
                </p>

                <div className="mt-12 mx-auto relative w-full max-w-sm aspect-[9/16] rounded-3xl overflow-hidden border border-slate-200 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)] bg-[#0a0d14]">
                    <video
                        data-testid="evolution-video"
                        src="https://customer-assets.emergentagent.com/job_bubble-notify-hub/artifacts/c25593ie_MBE%20ALERTS%20CTA%2001.mp4#t=0.1"
                        controls
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover"
                    />
                </div>
            </div>
        </section>
    );
}

function PositioningSection() {
    return (
        <section
            data-testid="section-positioning"
            className="relative bg-[var(--mbe-bg-dark)] text-white py-28 lg:py-40 overflow-hidden"
        >
            <div className="absolute inset-0 bg-grid-dark opacity-30" />
            <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[var(--mbe-gold)]/10 rounded-full blur-[140px]" />
            <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <span className="tag-pill bg-white/5 border border-white/10 text-slate-300">
                    More than a Telegram bot
                </span>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold mt-6 leading-[1.02]">
                    A timing layer. <br />
                    A workflow upgrade. <br />
                    <span className="text-[var(--mbe-gold)]">A trading edge.</span>
                </h2>
                <p className="mt-8 text-slate-400 max-w-2xl mx-auto text-lg">
                    MBE Alert solves the one thing every trader struggles with.
                    Being early enough to react, with enough structure to act
                    without panic.
                </p>
            </div>
        </section>
    );
}

function WhatIsSection() {
    const points = [
        "Real time Telegram pings the moment price enters a valid MBE bubble zone",
        "Each alert ships with pair, timeframe, and entry, already structured",
        "Built on top of the MBE system. Zero noise, only valid zones",
        "Designed for traders who can't sit at the screen all day",
    ];
    return (
        <section
            data-testid="section-what-is"
            className="bg-white text-[var(--mbe-text-light)] py-24 lg:py-32"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-center">
                <div className="lg:col-span-5">
                    <span className="tag-pill bg-amber-50 text-amber-700 border border-amber-200">
                        What Is MBE Alert?
                    </span>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-5 leading-tight">
                        A real time alert assistant for{" "}
                        <span className="text-[var(--mbe-gold-2)]">
                            valid MBE bubble zones.
                        </span>
                    </h2>
                    <p className="mt-5 text-slate-600 leading-relaxed">
                        Instead of watching the candles and second guessing your
                        trades, you get an alert the moment the price enters a
                        zone worth your attention. Open Telegram, read the
                        alert, decide with clarity.
                    </p>
                    <div className="mt-7">
                        <CTA
                            location="whatis-primary"
                            section="what-is"
                            label="Try It for 5 Days"
                        />
                    </div>
                </div>
                <div className="lg:col-span-7">
                    <ul className="space-y-4">
                        {points.map((p, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-4 p-5 rounded-2xl border border-slate-200 bg-slate-50/60 hover:border-[var(--mbe-gold)]/50 hover:bg-white transition"
                            >
                                <CheckCircle2 className="w-6 h-6 text-[var(--mbe-gold-2)] flex-shrink-0 mt-0.5" />
                                <span className="text-slate-700 leading-relaxed">
                                    {p}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}

function FeaturesSection() {
    const features = [
        { icon: Bell, title: "Real Time Telegram Alerts", desc: "Notifies you the moment an entry zone is being triggered. Not even a candle late." },
        { icon: Target, title: "Pair Included", desc: "Every alert tells you exactly which pair is being triggered." },
        { icon: Clock, title: "Timeframe Included", desc: "M15, M30, H1. Timeframes at a glance, no guessing." },
        { icon: LineChart, title: "Entry Price Visibility", desc: "Know exactly when to enter." },
        { icon: Layers, title: "Clean Structured Format", desc: "Same layout, every alert. View, decide, enter. Actions under 5 seconds." },
        { icon: Coffee, title: "Built for Busy Traders", desc: "Made for traders with jobs, families, and a life off the chart." },
        { icon: Sparkles, title: "Fresh Opportunity Focus", desc: "Only fresh zones, never stale bubbles you already missed." },
        { icon: Workflow, title: "Better Workflow, Less Stress", desc: "Less screen time. More confident executions." },
    ];
    return (
        <section
            data-testid="section-features"
            id="features"
            className="bg-[var(--mbe-bg-light-2)] text-[var(--mbe-text-light)] py-24 lg:py-32"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="tag-pill bg-white text-slate-600 border border-slate-200">
                        Inside MBE Alert
                    </span>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-5 leading-tight">
                        Everything you need.
                        <span className="text-[var(--mbe-gold-2)]"> Nothing you don&apos;t.</span>
                    </h2>
                </div>

                <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {features.map((f, i) => (
                        <div
                            key={i}
                            data-testid={`feature-card-${i}`}
                            className="group bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(15,23,42,0.3)] hover:border-[var(--mbe-gold)]/40 transition-all duration-300"
                        >
                            <div className="w-11 h-11 rounded-xl bg-amber-50 grid place-items-center group-hover:bg-[var(--mbe-gold)] transition">
                                <f.icon className="w-5 h-5 text-[var(--mbe-gold-2)] group-hover:text-[#0a0d14] transition" />
                            </div>
                            <h3 className="mt-5 font-display font-semibold text-lg">
                                {f.title}
                            </h3>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                                {f.desc}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function BonusSection() {
    return (
        <section
            data-testid="section-bonus"
            id="bonus"
            className="relative bg-[var(--mbe-bg-dark)] text-white py-24 lg:py-32 overflow-hidden"
        >
            <div className="absolute inset-0 bg-grid-dark opacity-30" />
            <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="rounded-[2rem] border border-[var(--mbe-gold)]/30 bg-gradient-to-br from-[#131824] to-[#0e1320] p-8 sm:p-12 lg:p-16 relative overflow-hidden">
                    <div className="absolute -right-20 -top-20 w-72 h-72 bg-[var(--mbe-gold)]/15 rounded-full blur-3xl" />
                    <div className="relative grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <span className="tag-pill bg-[var(--mbe-gold)]/15 text-[var(--mbe-gold)] border border-[var(--mbe-gold)]/40">
                                <Sparkles className="w-3.5 h-3.5" />
                                Trial Bonus · Free
                            </span>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-5 leading-tight">
                                Free Live-Trade Webinar
                                <br />
                                <span className="text-[var(--mbe-gold)]">
                                    Using MBE Alert.
                                </span>
                            </h2>
                            <ul className="mt-6 space-y-3 text-slate-300">
                                {[
                                    "How to read each alert without panicking",
                                    "How to plug it into your existing workflow",
                                    "How to avoid emotional, late entries",
                                    "Live walk-through of real bubble zone alerts",
                                ].map((t, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <CheckCircle2 className="w-5 h-5 text-[var(--mbe-gold)] flex-shrink-0 mt-0.5" />
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-8">
                                <CTA
                                    location="bonus-primary"
                                    section="bonus"
                                    label="Claim My Trial Access"
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="relative aspect-square rounded-3xl overflow-hidden border border-[var(--mbe-gold)]/30 bg-[#0a0d14] group">
                                <img
                                    data-testid="webinar-poster"
                                    src="https://customer-assets.emergentagent.com/job_bubble-notify-hub/artifacts/zgipkhq3_LIVE%20WEBINAR%20MBE%20ALERT.jpg"
                                    alt="MBE Alert Live Webinar — How to use MBE Alert plus bonus live trade, every Tuesday 9PM via Zoom"
                                    className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-[1.03]"
                                    loading="lazy"
                                    decoding="async"
                                />
                                <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-[#0a0d14] via-[#0a0d14]/70 to-transparent">
                                    <div className="flex items-center gap-2 text-xs text-slate-300">
                                        <Users className="w-4 h-4 text-[var(--mbe-gold)]" />
                                        Every Tuesday 9PM via Zoom · limited seats
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export {
    HeroSection,
    VideoEvolutionSection,
    PositioningSection,
    WhatIsSection,
    FeaturesSection,
    BonusSection,
    CTA,
    traders,
};
